import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  History,
  RotateCcw,
  Tags,
  Sparkles,
  Loader2,
  Pencil,
  Trash2,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AdminShell } from "@/components/AdminShell";
import { useConfirm } from "@/components/admin/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { DateCell } from "@/components/admin/DateCell";
import { categoryLabel, CATEGORIES } from "@/lib/categories";
import { AdReviewDialog } from "@/components/admin/AdReviewDialog";
import { AdMediaPanel } from "@/components/admin/AdMediaPanel";
import { Checkbox } from "@/components/ui/checkbox";
import { cn, FIELD_CLASS, FIELD_TEXTAREA_CLASS, FIELD_CHECKBOX_ROW_CLASS } from "@/lib/utils";
import {
  adminGetAdVersions,
  adminRevertAdField,
  adminGetAdTags,
  adminSetAdTags,
  adminRegenerateAdTags,
  adminReviewAd,
  adminUpdateAd,
  adminDeleteAd,
} from "@/lib/admin.functions";




export const Route = createFileRoute("/_authenticated/admin/annons/$slug")({
  head: () => ({
    meta: [
      { title: "Versionshistorik — Administration | Sellavo" },
      { name: "description", content: "Se och återställ tidigare versioner av en annons." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <AdminShell title="Versionshistorik">
      <VersionsBody />
    </AdminShell>
  ),
});

type Entry = {
  id: string;
  field: string;
  old_value: string | null;
  new_value: string | null;
  created_at: string;
  changed_by_admin?: boolean | null;
  changed_by?: string | null;
  editor_email?: string | null;

};

const FIELD_LABEL: Record<string, string> = {
  title: "Rubrik",
  description: "Beskrivning",
  price: "Pris",
  category: "Kategori",
  location: "Ort",
};

function display(field: string, value: string | null) {
  if (value === null || value === "") return "—";
  if (field === "price") return `${Number(value).toLocaleString("sv-SE")} kr`;
  if (field === "category") return categoryLabel(value);
  return value;
}

function shorten(text: string) {
  return text.length > 120 ? `${text.slice(0, 120)}…` : text;
}

/**
 * Slår ihop ändringar som gjordes vid samma tillfälle (samma person, inom 15
 * sekunder) så historiken visar hela versioner i stället för lösa fält.
 */
function groupHistory(entries: Entry[]): Entry[][] {
  const groups: Entry[][] = [];
  for (const entry of entries) {
    const last = groups[groups.length - 1];
    const prev = last?.[last.length - 1];
    const sameEditor =
      prev &&
      (prev.changed_by ?? null) === (entry.changed_by ?? null) &&
      !!prev.changed_by_admin === !!entry.changed_by_admin;
    const close =
      prev &&
      Math.abs(new Date(prev.created_at).getTime() - new Date(entry.created_at).getTime()) <= 15000;
    const sameField = last?.some((e) => e.field === entry.field);
    if (last && sameEditor && close && !sameField) last.push(entry);
    else groups.push([entry]);
  }
  return groups;
}


function VersionsBody() {
  const { confirm, dialog } = useConfirm();
  const { slug } = Route.useParams();
  const qc = useQueryClient();
  const fetchVersions = useServerFn(adminGetAdVersions);
  const revertFn = useServerFn(adminRevertAdField);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-ad-versions", slug],
    queryFn: () => fetchVersions({ data: { slug } }),
  });

  const revert = useMutation({
    mutationFn: (vars: { field: string; value: string }) =>
      revertFn({ data: { slug, field: vars.field as never, value: vars.value } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-ad-versions", slug] });
      qc.invalidateQueries({ queryKey: ["ad-history", slug] });
    },
  });

  if (isLoading) return <p className="text-muted-foreground">Laddar…</p>;
  if (!data) return <p className="text-muted-foreground">Annonsen hittades inte.</p>;

  const ad = data.ad as any;
  const history = (data.history ?? []) as Entry[];
  const owner = (data as any).owner as {
    id: string;
    name: string | null;
    email: string | null;
    adsCount: number;
  } | null;

  return (
    <div className="space-y-6">
      <Link
        to="/admin"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Tillbaka till översikten
      </Link>

      <section className="rounded-md border border-border p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-heading text-lg font-bold text-foreground">{ad.title}</h2>
            <p className="text-sm text-muted-foreground">
              {categoryLabel(ad.category ?? "ovrigt")} · {ad.location || "okänd ort"} ·{" "}
              {Number(ad.price).toLocaleString("sv-SE")} kr
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Skapad <DateCell value={ad.created_at} /> · senast ändrad{" "}
              <DateCell value={ad.updated_at} />
            </p>
            <p className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <User className="h-4 w-4 text-primary" />
              {owner ? (
                <span className="text-foreground">
                  <span className="font-medium">{owner.name || "Inget användarnamn"}</span>
                  <span className="text-muted-foreground">
                    {" "}
                    · {owner.email ?? "Okänd e-post"} · {owner.adsCount}{" "}
                    {owner.adsCount === 1 ? "annons" : "annonser"}
                  </span>
                </span>
              ) : (
                <span className="text-muted-foreground">
                  Annonsen saknar kopplad användare (skapad utan konto).
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/a/$slug"
              params={{ slug }}
              className="inline-flex items-center rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-accent"
            >
              Visa annons
            </Link>
            {owner && (
              <Link
                to="/admin/anvandare/$userId"
                params={{ userId: owner.id }}
                className="inline-flex items-center rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-accent"
              >
                Öppna ägaren
              </Link>
            )}
            {owner?.email && (
              <Link
                to="/admin/annonser"
                search={{ filter: "all" as const, q: owner.email }}
                className="inline-flex items-center rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-accent"
              >
                Ägarens annonser
              </Link>
            )}
          </div>
        </div>
      </section>

      <AdMediaPanel
        slug={slug}
        imageUrls={(ad.image_urls?.length ? ad.image_urls : [ad.image_url]).filter(Boolean)}
        videoUrl={ad.video_url ?? null}
      />

      <AdEditPanel ad={ad} slug={slug} />

      <AdTagsPanel slug={slug} />





      <section>
        <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
          <History className="h-4 w-4 text-primary" /> Ändringar ({history.length})
        </h3>
        {history.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Inga ändringar har gjorts på den här annonsen.
          </p>
        ) : (
          <ol className="mt-3 space-y-3">
            {groupHistory(history).map((group) => {
              const first = group[0];
              const byLabel = first.changed_by_admin
                ? `admin${first.editor_email ? ` · ${first.editor_email}` : ""}`
                : first.changed_by_admin === false || first.changed_by
                  ? `säljaren${
                      first.editor_email
                        ? ` · ${first.editor_email}`
                        : owner?.name || owner?.email
                          ? ` · ${owner.name || owner.email}`
                          : ""
                    }`
                  : "okänd användare";
              const restorable = group.filter((e) => e.old_value && FIELD_LABEL[e.field]);
              return (
                <li key={first.id} className="rounded-md border border-border p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-foreground">
                      Ändring av{" "}
                      {group.map((e) => FIELD_LABEL[e.field] ?? e.field).join(", ").toLowerCase()}
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                          first.changed_by_admin
                            ? "bg-primary/15 text-primary"
                            : "bg-muted text-muted-foreground",
                        )}
                      >
                        {byLabel}
                      </span>
                    </p>
                    <span className="text-xs text-muted-foreground">
                      <DateCell value={first.created_at} />
                    </span>
                  </div>

                  <div className="mt-3 space-y-3">
                    {group.map((e) => (
                      <div key={e.id}>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {FIELD_LABEL[e.field] ?? e.field}
                        </p>
                        <div className="mt-1 grid gap-2 sm:grid-cols-2">
                          <div className="rounded bg-destructive/10 p-2">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-destructive">
                              Före
                            </p>
                            <p className="mt-1 whitespace-pre-line break-words text-sm text-foreground">
                              {display(e.field, e.old_value)}
                            </p>
                          </div>
                          <div className="rounded bg-emerald-500/10 p-2">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">
                              Efter
                            </p>
                            <p className="mt-1 whitespace-pre-line break-words text-sm text-foreground">
                              {display(e.field, e.new_value)}
                            </p>
                          </div>
                        </div>
                        {e.old_value && FIELD_LABEL[e.field] && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2"
                            disabled={revert.isPending}
                            onClick={async () => {
                              const ok = await confirm({
                                title: `Återställ ${FIELD_LABEL[e.field].toLowerCase()}?`,
                                description:
                                  `Ändringen gjordes av ${byLabel}.\n\n` +
                                  `Nu: ${shorten(display(e.field, e.new_value))}\n` +
                                  `Återställs till: ${shorten(display(e.field, e.old_value))}\n\n` +
                                  "Återställningen loggas som en ny ändring i historiken, så inget försvinner.",
                                confirmLabel: "Återställ",
                                destructive: false,
                              });
                              if (ok) revert.mutate({ field: e.field, value: e.old_value! });
                            }}
                          >
                            <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Återställ bara detta fält
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  {restorable.length > 1 && (
                    <Button
                      size="sm"
                      className="mt-3"
                      disabled={revert.isPending}
                      onClick={async () => {
                        const ok = await confirm({
                          title: "Återställ hela versionen?",
                          description:
                            `Allt som ändrades vid det här tillfället av ${byLabel} återställs:\n` +
                            restorable
                              .map(
                                (e) =>
                                  `• ${FIELD_LABEL[e.field]}: ${shorten(display(e.field, e.old_value))}`,
                              )
                              .join("\n") +
                            "\n\nÅterställningen loggas som nya ändringar i historiken.",
                          confirmLabel: "Återställ hela versionen",
                          destructive: false,
                        });
                        if (!ok) return;
                        for (const e of restorable) {
                          await revert.mutateAsync({ field: e.field, value: e.old_value! });
                        }
                      }}
                    >
                      <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Återställ hela versionen (
                      {restorable.length} fält)
                    </Button>
                  )}
                </li>
              );
            })}
          </ol>
        )}
        {revert.error instanceof Error && (
          <p className="mt-3 text-sm text-destructive">{revert.error.message}</p>
        )}
      </section>

      {dialog}
    </div>
  );
}

/** Interna AI-taggar – endast synliga i administrationen, används för matchning av liknande annonser. */
function AdTagsPanel({ slug }: { slug: string }) {
  const getTags = useServerFn(adminGetAdTags);
  const setTags = useServerFn(adminSetAdTags);
  const regenerate = useServerFn(adminRegenerateAdTags);
  const [value, setValue] = useState("");
  const [dirty, setDirty] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-ad-tags", slug],
    queryFn: () => getTags({ data: { slug } }),
  });

  useEffect(() => {
    if (data && !dirty) setValue((data.tags ?? []).join(", "));
  }, [data, dirty]);

  const save = useMutation({
    mutationFn: () =>
      setTags({
        data: {
          slug,
          tags: value
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
            .slice(0, 12),
        },
      }),
    onSuccess: (res) => {
      setValue((res.tags ?? []).join(", "));
      setDirty(false);
    },
  });

  const regen = useMutation({
    mutationFn: () => regenerate({ data: { slug } }),
    onSuccess: (res) => {
      setValue((res.tags ?? []).join(", "));
      setDirty(false);
    },
  });

  return (
    <section className="rounded-md border border-border p-4">
      <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
        <Tags className="h-4 w-4 text-primary" /> Interna taggar
      </h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Visas aldrig för besökare. Används bara för att matcha liknande annonser. Separera med komma
        (max 8 sparas).
      </p>
      {isLoading ? (
        <p className="mt-3 text-sm text-muted-foreground">Laddar…</p>
      ) : (
        <>
          <input
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setDirty(true);
            }}
            placeholder="lego, byggsats, barn"
            className={cn(FIELD_CLASS, "mt-3")}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" disabled={save.isPending || !dirty} onClick={() => save.mutate()}>
              {save.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Spara
              taggar
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={regen.isPending}
              onClick={() => regen.mutate()}
            >
              {regen.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              )}
              Generera om med AI
            </Button>
          </div>
          {(save.error instanceof Error || regen.error instanceof Error) && (
            <p className="mt-2 text-sm text-destructive">
              {(save.error as Error)?.message ?? (regen.error as Error)?.message}
            </p>
          )}
        </>
      )}
    </section>
  );
}

/** Redigera annonsens innehåll och status direkt i administrationen. */
function AdEditPanel({ ad, slug }: { ad: any; slug: string }) {
  const { confirm, dialog } = useConfirm();
  const qc = useQueryClient();
  const updateAd = useServerFn(adminUpdateAd);
  const deleteAd = useServerFn(adminDeleteAd);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: ad.title ?? "",
    description: ad.description ?? "",
    price: String(ad.price ?? 0),
    category: ad.category ?? "ovrigt",
    location: ad.location ?? "",
    is_paused: !!ad.is_paused,
  });

  const [reviewOpen, setReviewOpen] = useState(false);
  const reviewFn = useServerFn(adminReviewAd);
  const review = useMutation({
    mutationFn: () =>
      reviewFn({
        data: {
          slug,
          title: form.title.trim(),
          description: form.description.trim(),
          price: Math.round(Number(form.price) || 0),
          category: form.category,
          location: form.location.trim(),
        },
      }),
  });


  const save = useMutation({
    mutationFn: async () => {
      if (!form.location.trim()) throw new Error("Annonsen måste ha en ort.");
      if (!form.title.trim()) throw new Error("Annonsen måste ha en rubrik.");
      return updateAd({
        data: {
          slug,
          title: form.title.trim(),
          description: form.description.trim(),
          price: Math.round(Number(form.price) || 0),
          category: form.category,
          location: form.location.trim(),
          is_paused: form.is_paused,
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-ad-versions", slug] });
      qc.invalidateQueries({ queryKey: ["admin-ads-page"] });
      qc.invalidateQueries({ queryKey: ["admin-ads"] });
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteAd({ data: { slug } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-ads-page"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      navigate({ to: "/admin/annonser", search: { filter: "all" as const } });
    },
  });

  const field = cn(FIELD_CLASS, "mt-1");

  return (
    <section className="rounded-md border border-border p-4">
      <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
        <Pencil className="h-4 w-4 text-primary" /> Redigera annons
      </h3>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-sm sm:col-span-2">
          <span className="text-muted-foreground">Rubrik</span>
          <input
            className={field}
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          />
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="text-muted-foreground">Beskrivning</span>
          <textarea
            rows={6}
            className={cn(FIELD_TEXTAREA_CLASS, "mt-1")}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
        </label>
        <label className="text-sm">
          <span className="text-muted-foreground">Pris (kr)</span>
          <input
            type="number"
            className={field}
            value={form.price}
            onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
          />
        </label>
        <label className="text-sm">
          <span className="text-muted-foreground">Kategori</span>
          <Select
            value={form.category}
            onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}
          >
            <SelectTrigger className="mt-1 w-full">
              <SelectValue placeholder="Välj kategori" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.slug} value={c.slug}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="text-muted-foreground">Ort (obligatorisk)</span>
          <input
            className={`${field} ${
              form.location.trim() ? "" : "border-amber-500 bg-amber-500/10"
            }`}
            value={form.location}
            onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
          />
          {!form.location.trim() && (
            <span className="mt-1 block text-xs text-amber-600">
              Alla annonser måste ha en ort.
            </span>
          )}
        </label>
      </div>

      <div className="mt-1 flex flex-wrap gap-4 text-sm">
        <label className={cn(FIELD_CHECKBOX_ROW_CLASS, "cursor-pointer")}>
          <Checkbox
            checked={form.is_paused}
            onCheckedChange={(v) => setForm((f) => ({ ...f, is_paused: v === true }))}
          />
          Pausad
        </label>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={save.isPending}
          onClick={async () => {
            const changed = [
              form.title.trim() !== (ad.title ?? "") && "rubrik",
              form.description.trim() !== (ad.description ?? "") && "beskrivning",
              Math.round(Number(form.price) || 0) !== Number(ad.price ?? 0) && "pris",
              form.category !== (ad.category ?? "ovrigt") && "kategori",
              form.location.trim() !== (ad.location ?? "") && "ort",
              form.is_paused !== !!ad.is_paused && "pausstatus",
            ].filter(Boolean) as string[];
            const ok = await confirm({
              title: "Spara ändringen som admin?",
              description: changed.length
                ? `Du ändrar ${changed.join(", ")} på en annons du inte äger. Originalet sparas i historiken nedan och du kan återställa det när som helst.`
                : "Inga fält verkar ändrade. Vill du spara ändå?",
              confirmLabel: "Spara ändringar",
              destructive: false,
            });
            if (ok) save.mutate();
          }}
        >
          {save.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Spara ändringar
        </Button>
        <Button
          size="sm"
          variant="destructive"
          disabled={remove.isPending}
          onClick={async () => {
            const ok = await confirm({
              title: "Ta bort annonsen?",
              description: `”${ad.title}” tas bort permanent. Det går inte att ångra.`,
              confirmLabel: "Ta bort annonsen",
            });
            if (ok) remove.mutate();
          }}
        >
          <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Ta bort annons
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={review.isPending}
          onClick={() => {
            setReviewOpen(true);
            review.mutate();
          }}
        >
          {review.isPending ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="mr-1.5 h-3.5 w-3.5" />
          )}
          Omvärdera med AI
        </Button>
      </div>
      <AdReviewDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        loading={review.isPending}
        error={review.error instanceof Error ? review.error.message : null}
        result={(review.data as any)?.review ?? null}
        imageCount={(review.data as any)?.imageCount}
        market={(review.data as any)?.market ?? null}
        current={{
          title: form.title,
          description: form.description,
          price: Math.round(Number(form.price) || 0),
          category: form.category,
        }}
        onApply={(patch: Record<string, string>) => setForm((f) => ({ ...f, ...patch }))}
      />

      {save.isSuccess && !save.isPending && (
        <p className="mt-2 text-sm text-emerald-600">Ändringarna är sparade.</p>
      )}
      {(save.error instanceof Error || remove.error instanceof Error) && (
        <p className="mt-2 text-sm text-destructive">
          {(save.error as Error)?.message ?? (remove.error as Error)?.message}
        </p>
      )}
      {dialog}
    </section>
  );
}
